import React from "react";
import { useRouter } from "next/router";
import {
  ArrowCircleLeftIcon,
  ArrowCircleRightIcon,
  PlayIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import {
  HeroUnit as PrimitiveHeroUnit,
  Skeleton,
} from "@gonoodle/gn-universe-ui";
import {
  PAGES,
  SECTIONS,
  HERO_UNIT_ITEM_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "react-responsive";
import useMeasure from "react-use-measure";

import { CONTENT_TYPES } from "../../../../constants";
import { WithContentLink, Link } from "../../../../containers";
import { getUtmParamsFromQueryParams } from "../../../../utils/analytics";
import { useDataLoader } from "../../index";
import { useLogEvent } from "../../../../contexts/Analytics";
import { getRoutePrefixByContentType } from "../../../../utils/route";

const getOriginalItemTitle = ({ contentType, content }) =>
  [CONTENT_TYPES.TAG, CONTENT_TYPES.CHANNEL, CONTENT_TYPES.TOPIC].includes(
    contentType,
  )
    ? content.name
    : content.title;

const PrimaryItemCardWithLink = WithContentLink(PrimitiveHeroUnit.PrimaryCard);
const SecondaryItemCardWithLink = WithContentLink(
  PrimitiveHeroUnit.SecondaryCard,
);

const swipePower = (offset, velocity) => Math.abs(offset) * velocity;
const swipeConfidenceThreshold = 7_000;

export default function Carousel({ fetchers }) {
  const router = useRouter();
  const [fetcher] = fetchers;
  const [currentSlideIndex, setCurrentSlideIndex] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const { data, isLoading } = useDataLoader(fetcher);
  const [slideRef, slideBounds] = useMeasure();
  const slides = Array.isArray(data) ? data[0] : data;
  const isDragEnabled =
    !useMediaQuery({ query: "(min-width: 1024px)" }) && slides?.length > 1;

  const buttonStyle =
    "w-[56px] text-white fill-black stroke-[0.75] cursor-pointer hover:fill-purple transition-color duration-300";

  const { logEvent: logCarouselClickedEvent } = useLogEvent({
    event: "Carousel Slide Changed",
    properties: {
      sourcePage: PAGES.HOME,
      sourcePageType: PAGES.HOME,
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      referrer: () => {},
    },
  });

  const paginate = (nextSlideIndex) => {
    logCarouselClickedEvent({
      title: `${slides[currentSlideIndex].slideData.title}${nextSlideIndex}`,
      sourceName: `${SECTIONS.CAROUSEL}${nextSlideIndex}`,
      sourceElement: `${SECTIONS.CAROUSEL_NAVIGATION}${nextSlideIndex}`,
    });

    setCurrentSlideIndex(nextSlideIndex);
  };

  if (isLoading) {
    return (
      <Skeleton className="lg:px-[60px] rounded-lg overflow-hidden h-[580px]" />
    );
  }

  return (
    <div className="relative overflow-hidden pb-8 lg:pb-0">
      <motion.div
        className={twMerge("flex", isDragEnabled && "gap-[1%]")}
        initial={false}
        ref={slideRef}
        animate={{ x: `-${currentSlideIndex * (isDragEnabled ? 96 : 100)}%` }}
        transition={{ duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
        drag={isDragEnabled ? "x" : false}
        dragConstraints={{
          // Adding 30px to reduce the rollback effect for the last slide, since we are using % values i had to come up with a magic number.
          left: -slideBounds.width * (slides.length - 1) + 30,
          right: 0,
        }}
        dragElastic={1}
        onDragStart={() => {
          setIsDragging(true);
        }}
        onDragEnd={(e, { offset, velocity }) => {
          const swipe = swipePower(offset.x, velocity.x);
          if (
            swipe < -swipeConfidenceThreshold &&
            currentSlideIndex + 1 < slides.length
          ) {
            paginate(currentSlideIndex + 1);
          } else if (
            swipe > swipeConfidenceThreshold &&
            currentSlideIndex - 1 > -1
          ) {
            paginate(currentSlideIndex - 1);
          }

          setIsDragging(false);
        }}
      >
        {slides.map(({ slideData, primaryItem, secondaryItems }, index) => {
          const carouselPosition = index + 1;
          const primaryItemLinkProps = {
            className: twMerge(
              "w-full h-fit",
              isDragging && "pointer-events-none",
            ),
            events: [
              {
                event: "Link Clicked",
                properties: {
                  title: getOriginalItemTitle(primaryItem),
                  sourceElement: `${HERO_UNIT_ITEM_TYPES.PRIMARY} - slide ${carouselPosition}`,
                  sourceName: SECTIONS.CAROUSEL,
                  sourcePage: PAGES.HOME,
                  sourcePageType: PAGES.HOME,
                  url: primaryItem.content.destinationUrl,
                  ...getUtmParamsFromQueryParams(router.query),
                },
              },
            ],
          };

          return (
            <div
              className={twMerge(
                "min-w-full rounded-lg ",
                isDragEnabled && "min-w-[95%]",
                isDragEnabled && currentSlideIndex - 1 === index && "ml-[2.5%]",
                isDragEnabled && currentSlideIndex + 1 === index && "mr-[2.5%]",
              )}
              key={index}
              style={{ backgroundColor: `#${slideData.backgroundColor}` }}
            >
              <PrimitiveHeroUnit.Root
                textTheme={slideData.textTheme}
                sponsorLogo={slideData.sponsorLogo}
                frame={true}
              >
                {[CONTENT_TYPES.CUSTOM_LINK].includes(
                  primaryItem.contentType,
                ) ? (
                  <Link
                    {...primaryItemLinkProps}
                    href={primaryItem.content.destinationUrl}
                  >
                    <PrimitiveHeroUnit.PrimaryCard
                      title={primaryItem.title}
                      description={primaryItem.description}
                      media={{
                        loopingVideo: primaryItem.loopingVideo,
                        backgroundImage: primaryItem.backgroundImage,
                      }}
                      textTheme={primaryItem.textTheme}
                      callToAction={primaryItem.ctaText}
                      sponsorLogo={primaryItem.sponsorLogo}
                    />
                  </Link>
                ) : (
                  <PrimaryItemCardWithLink
                    {...primaryItemLinkProps}
                    id={primaryItem.content.id}
                    slug={primaryItem.content.slug}
                    contentLinkAsBase={getRoutePrefixByContentType(
                      primaryItem.contentType,
                    )}
                    contentLinkHrefBase={`${getRoutePrefixByContentType(
                      primaryItem.contentType,
                    )}/[...identity]`}
                    linkParams={{
                      sourceElement: HERO_UNIT_ITEM_TYPES.PRIMARY,
                      sourceName: SECTIONS.HERO_UNIT,
                      sourcePage: PAGES.HOME,
                      sourcePageType: PAGES.HOME,
                    }}
                    title={primaryItem.title}
                    description={primaryItem.description}
                    media={{
                      loopingVideo: primaryItem.loopingVideo,
                      backgroundImage: primaryItem.backgroundImage,
                    }}
                    textTheme={primaryItem.textTheme}
                    callToAction={
                      <>
                        {primaryItem.ctaText}
                        {CONTENT_TYPES.VIDEO === primaryItem.contentType && (
                          <PlayIcon className="h-input-icon ml-xs" />
                        )}

                        {CONTENT_TYPES.ACTIVITY === primaryItem.contentType && (
                          <DownloadIcon className="h-input-icon ml-xs" />
                        )}
                      </>
                    }
                    sponsorLogo={primaryItem.sponsorLogo}
                  />
                )}

                <hr
                  className={twMerge(
                    "md:hidden opacity-50 mix-blend-hard-light",
                    primaryItem.textTheme !== "light" && "mix-blend-overlay",
                  )}
                />

                <PrimitiveHeroUnit.CardList
                  title={slideData.title}
                  description={slideData.description}
                >
                  {secondaryItems.map((item, itemIndex) => {
                    const sharedProps = {
                      key: `${item.content.id}_${itemIndex}`,
                      className: twMerge(
                        "w-full sm:w-[46%] lg:w-full",
                        isDragging && "pointer-events-none",
                      ),
                      events: [
                        {
                          event: "Link Clicked",
                          properties: {
                            title: getOriginalItemTitle(item),
                            sourceElement: `${HERO_UNIT_ITEM_TYPES.SECONDARY} - slide ${carouselPosition}`,
                            sourceName: SECTIONS.CAROUSEL,
                            sourcePage: PAGES.HOME,
                            sourcePageType: PAGES.HOME,
                            url: item.content.destinationUrl,
                            ...getUtmParamsFromQueryParams(router.query),
                          },
                        },
                      ],
                    };
                    if (item.contentType === CONTENT_TYPES.CUSTOM_LINK) {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <Link
                          href={item.content.destinationUrl}
                          {...sharedProps}
                        >
                          <PrimitiveHeroUnit.SecondaryCard
                            title={item.title}
                            description={item.description}
                            image={item.image}
                          />
                        </Link>
                      );
                    }
                    return (
                      // eslint-disable-next-line react/jsx-key
                      <SecondaryItemCardWithLink
                        contentLinkAsBase={getRoutePrefixByContentType(
                          item.contentType,
                        )}
                        contentLinkHrefBase={`${getRoutePrefixByContentType(
                          item.contentType,
                        )}/[...identity]`}
                        id={item.content.id}
                        slug={item.content.slug}
                        linkParams={{
                          sourceElement: `${HERO_UNIT_ITEM_TYPES.SECONDARY} - slide ${carouselPosition}`,
                          sourceName: SECTIONS.CAROUSEL,
                          sourcePage: PAGES.HOME,
                          sourcePageType: PAGES.HOME,
                        }}
                        title={item.title}
                        description={item.description}
                        image={item.image}
                        {...sharedProps}
                      />
                    );
                  })}
                </PrimitiveHeroUnit.CardList>
              </PrimitiveHeroUnit.Root>
            </div>
          );
        })}
      </motion.div>

      {slides.length > 1 && (
        <div className="hidden lg:block absolute top-1/2 inset-x-1 z-10 pointer-events-none">
          <div className="flex flex-row justify-between">
            <button
              aria-label="Previous Slide"
              onClick={() => {
                paginate(currentSlideIndex - 1);
              }}
            >
              <ArrowCircleLeftIcon
                className={`${buttonStyle} pointer-events-auto ${
                  currentSlideIndex === 0 ? "hidden" : ""
                }`}
              />
            </button>
            <button
              className="right-0"
              aria-label="Next Slide"
              onClick={() => {
                paginate(currentSlideIndex + 1);
              }}
            >
              <ArrowCircleRightIcon
                className={`${buttonStyle} pointer-events-auto ${
                  currentSlideIndex === slides.length - 1 ? "hidden" : ""
                }`}
              />
            </button>
          </div>
        </div>
      )}

      {slides.length > 1 && (
        <div className="flex justify-center absolute space-x-2 bottom-3 lg:bottom-[24px]  inset-x-0 z-10  ">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={twMerge(
                "w-2 h-2 rounded-full",
                index === currentSlideIndex ? "bg-purple-500" : "bg-gray-400",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
